import React, { useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
  resolveAttachmentUrl,
  getInvoiceAttachmentDisplayName,
  downloadInvoiceAttachment,
  downloadLocalAttachmentFile,
} from "../utils/invoiceAttachmentUtils";

import {
  handleFormattedGstBlur,
  handleFormattedNameBlur,
} from "../utils/nameFormatUtils";

export const ADD_NEW_SUPPLIER_VALUE = "__ADD_NEW_SUPPLIER__";

/**
 * Shared invoice + supplier block for medicine / pathology add-stock modals.
 */
const StockAddInvoiceSupplierSection = ({
  formik,
  invoiceTypeOptions = [],
  suppliers = [],
  selectedSupplierKey,
  onSupplierKeyChange,
  isNewSupplier,
  errorUpdateTrigger = 0,
  onFileChange,
  onDeleteAttachment,
  attachmentFile = null,
  attachmentUrl = "",
  showAttachment = true,
}) => {
  const fileInputRef = useRef(null);
  const attachmentName =
    formik.values.attachment && String(formik.values.attachment).trim()
      ? String(formik.values.attachment)
      : "";
  const resolvedUrl =
    attachmentUrl ||
    resolveAttachmentUrl(attachmentName) ||
    (attachmentName && !attachmentFile ? attachmentName : "");
  const displayName =
    getInvoiceAttachmentDisplayName(resolvedUrl || attachmentName) ||
    attachmentName;
  const canDownload =
    attachmentFile instanceof File || Boolean(resolvedUrl);

  const handleDownloadAttachment = async () => {
    try {
      if (attachmentFile instanceof File) {
        await downloadLocalAttachmentFile(attachmentFile);
      } else if (resolvedUrl) {
        await downloadInvoiceAttachment(resolvedUrl);
      }
    } catch {
      // Errors are toasted in download helpers
    }
  };

  const handleLocalFileChange = (event) => {
    onFileChange?.(event);
    // Reset so the same file can be selected again after remove / re-attach
    if (event?.target) {
      event.target.value = "";
    }
  };

  const handleLocalDeleteAttachment = () => {
    onDeleteAttachment?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  return (
    <>
      <Typography variant="subtitle1" fontWeight="bold" pt={0} pb={0.25}>
        Invoice Details
      </Typography>
      <Grid container spacing={1} sx={{ mb: 0.5 }} alignItems="flex-start">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            id="invoiceNumber"
            name="invoiceNumber"
            label="Invoice Number"
            value={formik.values.invoiceNumber}
            onChange={formik.handleChange}
            error={
              formik.touched.invoiceNumber && Boolean(formik.errors.invoiceNumber)
            }
            helperText={
              formik.touched.invoiceNumber && formik.errors.invoiceNumber
            }
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControl
            fullWidth
            error={
              formik.touched.invoiceDate && Boolean(formik.errors.invoiceDate)
            }
            key={`form-control-invoice-date-${errorUpdateTrigger}`}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Invoice Date *"
                value={
                  formik.values.invoiceDate
                    ? dayjs(formik.values.invoiceDate)
                    : null
                }
                onChange={(value) => {
                  const hasValue = !!value;
                  formik.setFieldValue(
                    "invoiceDate",
                    hasValue ? value.toDate() : null,
                    true
                  );
                  formik.setFieldTouched("invoiceDate", true, false);
                  if (hasValue) {
                    formik.setFieldError("invoiceDate", undefined);
                  } else {
                    formik.setFieldError(
                      "invoiceDate",
                      "Invoice Date is required"
                    );
                  }
                }}
                renderInput={(params) => {
                  const hasError =
                    formik.touched.invoiceDate &&
                    Boolean(formik.errors.invoiceDate);
                  return (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      required
                      error={hasError}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!!formik.values.invoiceDate && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  formik.setFieldValue("invoiceDate", null, true);
                                  formik.setFieldTouched("invoiceDate", true, false);
                                  formik.setFieldError(
                                    "invoiceDate",
                                    "Invoice Date is required"
                                  );
                                }}
                                sx={{ mr: 0.5 }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                      onBlur={(e) => {
                        params.onBlur?.(e);
                        formik.setFieldTouched("invoiceDate", true, false);
                        const hasDate =
                          formik.values.invoiceDate !== null &&
                          formik.values.invoiceDate !== undefined &&
                          formik.values.invoiceDate !== "";
                        if (hasDate) {
                          formik.setFieldError("invoiceDate", undefined);
                        } else {
                          formik.setFieldError(
                            "invoiceDate",
                            "Invoice Date is required"
                          );
                        }
                      }}
                    />
                  );
                }}
                key={`invoice-date-${errorUpdateTrigger}`}
              />
            </LocalizationProvider>
            {formik.touched.invoiceDate && formik.errors.invoiceDate && (
              <FormHelperText key={`helper-text-${errorUpdateTrigger}`}>
                {formik.errors.invoiceDate}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            size="small"
            id="invoiceType"
            name="invoiceType"
            label="Invoice Type"
            value={formik.values.invoiceType}
            onChange={formik.handleChange}
            error={
              formik.touched.invoiceType && Boolean(formik.errors.invoiceType)
            }
            helperText={
              formik.touched.invoiceType && formik.errors.invoiceType
            }
          >
            <MenuItem value="">Select Type</MenuItem>
            {invoiceTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {showAttachment ? (
        <Grid item xs={12} sm={3}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: 40,
              px: 0.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Tooltip
                title={
                  attachmentName
                    ? "Replace attachment"
                    : "Attach file (required)"
                }
              >
                <IconButton
                  component="label"
                  size="small"
                  color={
                    formik.touched.attachment && formik.errors.attachment
                      ? "error"
                      : "primary"
                  }
                >
                  <AttachFileIcon fontSize="small" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={handleLocalFileChange}
                  />
                </IconButton>
              </Tooltip>
              {attachmentName || displayName ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "text.primary",
                    }}
                    title={displayName || attachmentName}
                  >
                    {displayName || attachmentName}
                  </Typography>
                  {canDownload && (
                    <Tooltip title="Download attachment">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={handleDownloadAttachment}
                      >
                        <DownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove attachment">
                    <IconButton size="small" onClick={handleLocalDeleteAttachment}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Typography
                  variant="body2"
                  color={
                    formik.touched.attachment && formik.errors.attachment
                      ? "error"
                      : "text.secondary"
                  }
                >
                  Attachment *
                </Typography>
              )}
            </Box>
            {formik.touched.attachment && formik.errors.attachment && (
              <FormHelperText error sx={{ ml: 0.5, mt: 0 }}>
                {formik.errors.attachment}
              </FormHelperText>
            )}
          </Box>
        </Grid>
        ) : null}
      </Grid>

      <Typography variant="subtitle1" fontWeight="bold" pt={0.5} pb={0.25}>
        Supplier Details
      </Typography>
      <Grid container spacing={1} sx={{ mb: 0.5 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            id="supplierSelect"
            label="Supplier *"
            value={selectedSupplierKey || ""}
            onChange={(e) => onSupplierKeyChange(e.target.value)}
            error={
              formik.touched.supplierName && Boolean(formik.errors.supplierName)
            }
            helperText={
              formik.touched.supplierName && formik.errors.supplierName
            }
          >
            <MenuItem value="">Select supplier</MenuItem>
            <MenuItem value={ADD_NEW_SUPPLIER_VALUE}>
              + Add new supplier
            </MenuItem>
            {suppliers.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.name || s.supplierName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {isNewSupplier && (
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              id="supplierName"
              name="supplierName"
              label="Supplier Name *"
              value={formik.values.supplierName}
              onChange={formik.handleChange}
              onBlur={(e) =>
                handleFormattedNameBlur(e, {
                  setFieldValue: formik.setFieldValue,
                  handleBlur: formik.handleBlur,
                })
              }
              error={
                formik.touched.supplierName &&
                Boolean(formik.errors.supplierName)
              }
              helperText={
                formik.touched.supplierName && formik.errors.supplierName
              }
            />
          </Grid>
        )}

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            id="supplierContact"
            name="supplierContact"
            label="Supplier Contact *"
            placeholder="10 digit phone number"
            value={formik.values.supplierContact}
            disabled={!isNewSupplier}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 10);
              formik.setFieldValue("supplierContact", value);
            }}
            error={
              formik.touched.supplierContact &&
              Boolean(formik.errors.supplierContact)
            }
            helperText={
              formik.touched.supplierContact && formik.errors.supplierContact
                ? formik.errors.supplierContact
                : isNewSupplier
                  ? "Enter 10 digit phone number"
                  : ""
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            id="supplierGstNumber"
            name="supplierGstNumber"
            label="GST Number *"
            value={formik.values.supplierGstNumber}
            disabled={!isNewSupplier}
            onChange={formik.handleChange}
            onBlur={(e) =>
              handleFormattedGstBlur(e, {
                setFieldValue: formik.setFieldValue,
                handleBlur: formik.handleBlur,
              })
            }
            error={
              formik.touched.supplierGstNumber &&
              Boolean(formik.errors.supplierGstNumber)
            }
            helperText={
              formik.touched.supplierGstNumber &&
              formik.errors.supplierGstNumber
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            id="supplierAddress"
            name="supplierAddress"
            label="Supplier Address *"
            multiline
            minRows={1}
            maxRows={2}
            value={formik.values.supplierAddress}
            disabled={!isNewSupplier}
            onChange={formik.handleChange}
            onBlur={(e) =>
              handleFormattedNameBlur(e, {
                setFieldValue: formik.setFieldValue,
                handleBlur: formik.handleBlur,
              })
            }
            error={
              formik.touched.supplierAddress &&
              Boolean(formik.errors.supplierAddress)
            }
            helperText={
              formik.touched.supplierAddress && formik.errors.supplierAddress
            }
          />
        </Grid>
      </Grid>
    </>
  );
};

export default StockAddInvoiceSupplierSection;
